import { useTranslation } from 'react-i18next';
import SettingsLayout from '@/components/settings-layout';
import { localTenant, updateMe } from '@/services/session-service';
import { Tenant } from '@turizi/shared';
import { useState } from 'react';
import { setWebTitle } from '@/lib/format-utils';
import { TextInput, Button, ActionIcon } from '@mantine/core';
import { useUpdateTenant } from '@/hooks/api-hooks';
import SettingsCard from '@/components/settings-card';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { notify } from '@/lib/notify';

export default function SettingsServiceOrdersPage(): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    const updateTenantMutation = useUpdateTenant();
    const [draft, setDraft] = useState<Tenant | null>(tenant);

    setWebTitle(t('service_orders'));

    const handleSave = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                service_order: {
                    extra_data: extraRows
                }
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success', { defaultValue: 'Settings saved successfully' }), t('success'));
            }
        });
    };

    const extraRows = draft?.settings?.service_order?.extra_data || [];

    const handleAddRow = () => {
        setDraft(prev => {
            if (!prev) return null;
            const currentServiceOrder = prev.settings.service_order || { extra_data: [] };
            const currentExtraData = currentServiceOrder.extra_data || [];
            return {
                ...prev,
                settings: {
                    ...prev.settings,
                    service_order: {
                        ...currentServiceOrder,
                        extra_data: [...currentExtraData, { key: '', value: '' }]
                    }
                }
            };
        });
    };

    const handleUpdateRow = (index: number, field: 'key' | 'value', value: string) => {
        setDraft(prev => {
            if (!prev) return null;
            const currentServiceOrder = prev.settings.service_order || { extra_data: [] };
            const currentExtraData = [...(currentServiceOrder.extra_data || [])];
            currentExtraData[index] = {
                ...currentExtraData[index],
                [field]: value
            };
            return {
                ...prev,
                settings: {
                    ...prev.settings,
                    service_order: {
                        ...currentServiceOrder,
                        extra_data: currentExtraData
                    }
                }
            };
        });
    };

    const handleDeleteRow = (index: number) => {
        setDraft(prev => {
            if (!prev) return null;
            const currentServiceOrder = prev.settings.service_order || { extra_data: [] };
            const currentExtraData = (currentServiceOrder.extra_data || []).filter((_, idx) => idx !== index);
            return {
                ...prev,
                settings: {
                    ...prev.settings,
                    service_order: {
                        ...currentServiceOrder,
                        extra_data: currentExtraData
                    }
                }
            };
        });
    };

    return (
        <div className="panel-content max-w-5xl">
            <SettingsLayout>
                <div className='flex flex-col gap-6 w-full'>
                    <SettingsCard
                        title={t('manifest_extra_rows', 'Manifest Custom Rows')}
                        description={t('manifest_extra_rows_description', 'Add extra custom information rows (such as signatures, legal clauses, or payment terms) printed at the bottom of the Manifest Service Orders.')}
                        onSave={handleSave}
                        saving={updateTenantMutation.isPending}
                    >
                        <div className="space-y-4">
                            {extraRows.length === 0 ? (
                                <div className="text-xs text-slate-400 italic py-6 text-center border border-dashed border-slate-200 rounded-xl">
                                    {t('no_extra_rows_defined', 'No custom rows defined yet.')}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {extraRows.map((row, idx) => (
                                        <div key={idx} className="flex gap-4 items-end">
                                            <TextInput
                                                className="flex-1 font-semibold"
                                                label={t('row_label', 'Row Label')}
                                                placeholder={t('example_signature_title', 'e.g. Signature Coordinator')}
                                                value={row.key || ''}
                                                onChange={(e) => handleUpdateRow(idx, 'key', e.target.value)}
                                            />
                                            <TextInput
                                                className="flex-1 font-medium"
                                                label={t('row_value', 'Row Value (Default)')}
                                                placeholder={t('example_signature_value', 'e.g. Leave empty for signature line')}
                                                value={row.value || ''}
                                                onChange={(e) => handleUpdateRow(idx, 'value', e.target.value)}
                                            />
                                            <ActionIcon
                                                color="red"
                                                variant="light"
                                                size="lg"
                                                onClick={() => handleDeleteRow(idx)}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Button
                                leftSection={<IconPlus size={16} />}
                                variant="outline"
                                color="black"
                                size="xs"
                                onClick={handleAddRow}
                            >
                                {t('add_row', 'Add Row')}
                            </Button>
                        </div>
                    </SettingsCard>
                </div>
            </SettingsLayout>
        </div>
    );
}
